import { useEffect, useState, type SyntheticEvent } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { AlertTriangle, Bot, Check, CheckCircle2, Copy, Download, Eye, Hash, KeyRound, Lock, ShieldCheck, SlidersHorizontal, UserMinus, X } from "lucide-react";
import { api } from "../../../../convex/_generated/api";
import { Avatar } from "../ui/Avatar";
import { Button } from "../ui/button";
import { Dialog } from "../ui/Dialog";
import { errorMessage } from "./format";
import type { MeReady } from "./types";

type Tab = "members" | "invitations" | "agents" | "secrets" | "security";

export function AdminDialog({ me, onClose }: { me: MeReady; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("members");
  return (
    <Dialog title="Workspace" onClose={onClose} wide>
      <div className="tabs" role="tablist">
        {(["members", "invitations", "agents", "secrets", "security"] as const).map((candidate) => (
          <button key={candidate} type="button" role="tab" aria-selected={tab === candidate} className={tab === candidate ? "is-active" : undefined} onClick={() => { setTab(candidate); }}>{candidate === "members" ? "Members" : candidate === "invitations" ? "Invitations" : candidate === "agents" ? "AI employees" : candidate === "secrets" ? "Secrets" : "Security"}</button>
        ))}
      </div>
      {tab === "members" ? <MembersTab me={me} /> : tab === "invitations" ? <InvitationsTab /> : tab === "agents" ? <AgentsTab /> : tab === "secrets" ? <SecretsTab /> : <SecurityTab me={me} />}
    </Dialog>
  );
}

function MembersTab({ me }: { me: MeReady }) {
  const members = useQuery(api.communication.listMembers, {});
  const updateRole = useMutation(api.communication.updateMemberRole);
  const deactivate = useMutation(api.communication.deactivateMember);
  const [error, setError] = useState<string | null>(null);
  const [accessPrincipalId, setAccessPrincipalId] = useState<string | null>(null);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const humans = (members ?? []).filter((member) => member.kind === "human");
  const run = async (action: () => Promise<unknown>) => { setError(null); try { await action(); } catch (caught) { setError(errorMessage(caught)); } };
  return (
    <div className="stack">
      {selectedPeople.length > 0 ? <div className="bulk-action-bar"><strong>{selectedPeople.length} selected</strong><Button size="compact" variant="ghost" onClick={() => { if (window.confirm(`Remove ${selectedPeople.length} selected people from Yodl?`)) void run(async () => { for (const principalId of selectedPeople) await deactivate({ principalId }); setSelectedPeople([]); }); }}><UserMinus size={13} /> Remove selected</Button></div> : null}
      <table className="data-table">
        <thead><tr><th aria-label="Select" /><th>Person</th><th>Email</th><th>Role</th><th>Channel access</th><th aria-label="Actions" /></tr></thead>
        <tbody>
          {humans.map((member) => (
            <tr key={member.principalId} className={member.active ? undefined : "is-inactive"}>
              <td>{member.role !== "owner" && member.active && member.principalId !== me.principalId ? <input type="checkbox" aria-label={`Select ${member.displayName}`} checked={selectedPeople.includes(member.principalId)} onChange={(event) => { setSelectedPeople((current) => event.target.checked ? [...current, member.principalId] : current.filter((id) => id !== member.principalId)); }} /> : null}</td>
              <td><span className="cell-person"><Avatar name={member.displayName} small photoUrl={member.avatarUrl} /><strong>{member.displayName}{member.principalId === me.principalId ? " (you)" : ""}</strong></span></td>
              <td>{member.email ?? "—"}</td>
              <td>
                {member.role === "owner" || !member.active || member.principalId === me.principalId ? <span className="status-badge">{member.active ? member.role : "removed"}</span> : (
                  <select name={`role-${member.principalId}`} value={member.role} onChange={(event) => { void run(() => updateRole({ principalId: member.principalId, role: event.target.value === "admin" ? "admin" : "member" })); }} aria-label={`Role for ${member.displayName}`}>
                    <option value="member">member</option><option value="admin">admin</option>
                  </select>
                )}
              </td>
              <td>
                {member.role === "owner" ? <span className="access-summary"><Eye size={12} /> All channels</span> : member.active && member.principalId !== me.principalId ? (
                  <Button size="compact" variant="ghost" onClick={() => { setAccessPrincipalId((current) => current === member.principalId ? null : member.principalId); }}>
                    <SlidersHorizontal size={13} /> {accessPrincipalId === member.principalId ? "Close" : "Manage"}
                  </Button>
                ) : <span className="nav-hint">—</span>}
              </td>
              <td>{member.role !== "owner" && member.active && member.principalId !== me.principalId ? <Button size="compact" variant="ghost" onClick={() => { if (window.confirm(`Remove ${member.displayName} from the workspace? They lose access immediately.`)) void run(() => deactivate({ principalId: member.principalId })); }}><UserMinus size={13} /> Remove</Button> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {accessPrincipalId !== null ? (
        <MemberChannelAccessEditor
          principalId={accessPrincipalId}
          name={humans.find((member) => member.principalId === accessPrincipalId)?.displayName ?? "Team member"}
          onClose={() => { setAccessPrincipalId(null); }}
        />
      ) : null}
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
    </div>
  );
}

function InvitationsTab() {
  const invitations = useQuery(api.communication.listInvitations, {});
  const channels = useQuery(api.communication.listChannelsForAdmin, {});
  const invite = useMutation(api.communication.invite);
  const revoke = useMutation(api.communication.revokeInvitation);
  const resendEmail = useMutation(api.communication.resendInvitationEmail);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "admin">("member");
  const [channelAccessMode, setChannelAccessMode] = useState<"all" | "selected">("selected");
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [accessInvitationId, setAccessInvitationId] = useState<string | null>(null);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const emails = [...new Set(email.split(/[\s,;]+/u).map((value) => value.trim().toLowerCase()).filter(Boolean))].slice(0, 50);
      if (emails.length === 0) throw new Error("Enter at least one email address.");
      for (const recipient of emails) await invite({ email: recipient, role, channelAccessMode, channelIds: channelAccessMode === "all" ? [] : channelIds });
      setNotice(`${emails.length} ${emails.length === 1 ? "invitation is" : "invitations are"} being emailed now.`);
      setEmail("");
      setChannelIds([]);
    } catch (caught) { setError(errorMessage(caught)); }
  };
  return (
    <div className="stack">
      <form className="invite-form" onSubmit={(event) => { void submit(event); }}>
        <div className="field-row invite-form__identity">
          <textarea name="invitation-email" autoComplete="off" spellCheck={false} value={email} onChange={(event) => { setEmail(event.target.value); }} placeholder="One email, or paste several separated by commas" aria-label="Email to invite" rows={2} required />
          <select name="invitation-role" value={role} onChange={(event) => { setRole(event.target.value === "admin" ? "admin" : "member"); }} aria-label="Workspace role"><option value="member">Member</option><option value="admin">Administrator</option></select>
        </div>
        <ChannelAccessPicker channels={channels ?? []} mode={channelAccessMode} selectedIds={channelIds} onModeChange={setChannelAccessMode} onSelectionChange={setChannelIds} />
        <div className="invite-form__actions">
          <span className="nav-hint">They can only open the channels you allow here.</span>
          <Button type="submit" variant="primary" disabled={channels === undefined || (channelAccessMode === "selected" && channelIds.length === 0)}>Invite</Button>
        </div>
      </form>
      {notice !== null ? <p className="form-notice" role="status">{notice}</p> : null}
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
      <table className="data-table">
        <thead><tr><th>Email</th><th>Role</th><th>Channel access</th><th>Invitation</th><th>Email</th><th aria-label="Actions" /></tr></thead>
        <tbody>
          {(invitations ?? []).length === 0 ? <tr><td colSpan={6} className="nav-hint">No invitations yet.</td></tr> : null}
          {(invitations ?? []).map((invitation) => (
            <tr key={invitation.id}>
              <td>{invitation.email}</td><td>{invitation.role}</td>
              <td><span className="access-summary"><Eye size={12} />{invitation.channelAccessMode === "all" ? "All channels" : invitation.channelAccessMode === "selected" ? `${invitation.channelIds.length} selected` : "Workspace defaults"}</span></td>
              <td><span className={invitation.status === "open" ? "status-badge status-badge--green" : "status-badge"}>{invitation.status}</span></td>
              <td><span className={invitation.emailDeliveryState === "sent" ? "status-badge status-badge--green" : invitation.emailDeliveryState === "failed" ? "status-badge status-badge--danger" : "status-badge"} title={invitation.emailDeliveryError}>{invitation.emailDeliveryState ?? "not sent"}</span></td>
              <td className="cell-actions">
                {invitation.status === "open" ? <Button size="compact" variant="ghost" onClick={() => { setAccessInvitationId((current) => current === invitation.id ? null : invitation.id); }}><SlidersHorizontal size={13} /> Access</Button> : null}
                {invitation.status === "open" && invitation.emailDeliveryState !== "queued" && invitation.emailDeliveryState !== "sent" ? <Button size="compact" variant="ghost" onClick={() => { void resendEmail({ invitationId: invitation.id }).catch((caught: unknown) => { setError(errorMessage(caught)); }); }}>{invitation.emailDeliveryState === "failed" ? "Retry email" : "Send email"}</Button> : null}
                {invitation.status === "open" ? <Button size="compact" variant="ghost" onClick={() => { void revoke({ invitationId: invitation.id }); }}>Revoke</Button> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {accessInvitationId !== null ? (() => {
        const invitation = (invitations ?? []).find((candidate) => candidate.id === accessInvitationId);
        return invitation === undefined ? null : (
          <InvitationChannelAccessEditor
            key={invitation.id}
            invitationId={invitation.id}
            email={invitation.email}
            initialMode={invitation.channelAccessMode ?? "selected"}
            initialChannelIds={invitation.channelIds}
            onClose={() => { setAccessInvitationId(null); }}
          />
        );
      })() : null}
    </div>
  );
}

type AdminChannel = NonNullable<ReturnType<typeof useQuery<typeof api.communication.listChannelsForAdmin>>>[number];

function ChannelAccessPicker({ channels, mode, selectedIds, onModeChange, onSelectionChange }: {
  channels: AdminChannel[];
  mode: "all" | "selected";
  selectedIds: string[];
  onModeChange: (mode: "all" | "selected") => void;
  onSelectionChange: (ids: string[]) => void;
}) {
  const selected = new Set(selectedIds);
  const toggle = (id: string) => { onSelectionChange(selected.has(id) ? selectedIds.filter((candidate) => candidate !== id) : [...selectedIds, id]); };
  const presets = [
    { label: "Reception", names: ["general", "welcome", "apex-reception", "reception"] },
    { label: "Provider", names: ["general", "apex-clinical-staff", "clinical-staff", "apex-providers", "providers"] },
    { label: "Accounting", names: ["general", "apex-accounting", "accounting"] },
    { label: "Marketing", names: ["general", "apex-marketing", "marketing"] }
  ];
  const applyPreset = (names: string[]) => {
    const normalized = new Set(names);
    onModeChange("selected");
    onSelectionChange(channels.filter((channel) => normalized.has(channel.name.trim().toLowerCase())).map((channel) => channel.id));
  };
  return (
    <fieldset className="channel-access-picker">
      <legend>Channel access</legend>
      <div className="permission-presets"><span>Quick role:</span>{presets.map((preset) => <button key={preset.label} type="button" onClick={() => { applyPreset(preset.names); }}>{preset.label}</button>)}</div>
      <div className="access-mode-options">
        <label className={mode === "selected" ? "is-selected" : undefined}>
          <input type="radio" name="channel-access-mode" checked={mode === "selected"} onChange={() => { onModeChange("selected"); }} />
          <span><strong>Only selected channels</strong><small>Best for limiting sensitive information.</small></span>
        </label>
        <label className={mode === "all" ? "is-selected" : undefined}>
          <input type="radio" name="channel-access-mode" checked={mode === "all"} onChange={() => { onModeChange("all"); }} />
          <span><strong>All channels</strong><small>Includes channels created later.</small></span>
        </label>
      </div>
      {mode === "selected" ? (
        <div className="channel-selection">
          <div className="channel-selection__header">
            <span>{selectedIds.length} of {channels.length} selected</span>
            <span><button type="button" onClick={() => { onSelectionChange(channels.map((channel) => channel.id)); }}>Select all</button><button type="button" onClick={() => { onSelectionChange([]); }}>Clear</button></span>
          </div>
          <div className="channel-checklist">
            {channels.length === 0 ? <p className="nav-hint">Create a channel before inviting this person.</p> : channels.map((channel) => (
              <label key={channel.id}>
                <input type="checkbox" checked={selected.has(channel.id)} onChange={() => { toggle(channel.id); }} />
                {channel.type === "private_channel" ? <Lock size={13} /> : <Hash size={13} />}
                <span><strong>{channel.name}</strong><small>{channel.type === "private_channel" ? "Private" : "Workspace"} · {channel.classification === "phi" ? "Protected health information" : channel.classification === "confidential" ? "Confidential" : "Standard"}</small></span>
              </label>
            ))}
          </div>
        </div>
      ) : <p className="permission-warning"><Eye size={14} /> This person will see every current channel and automatically receive access to new channels.</p>}
    </fieldset>
  );
}

function MemberChannelAccessEditor({ principalId, name, onClose }: { principalId: string; name: string; onClose: () => void }) {
  const channels = useQuery(api.communication.listChannelsForAdmin, {});
  const access = useQuery(api.communication.getMemberChannelAccess, { principalId });
  const updateAccess = useMutation(api.communication.updateMemberChannelAccess);
  const [mode, setMode] = useState<"all" | "selected">("selected");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (access === undefined) return;
    setMode(access.mode);
    setSelectedIds(access.channelIds);
  }, [access]);
  const save = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await updateAccess({ principalId, mode, channelIds: mode === "all" ? [] : selectedIds });
      setNotice(`${name}'s channel access was updated.`);
    } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };
  return (
    <section className="member-access-editor" aria-label={`Channel access for ${name}`}>
      <header><div><strong>Channel access for {name}</strong><span>Changes take effect immediately.</span></div><Button size="icon" variant="ghost" aria-label="Close channel access" onClick={onClose}><X size={15} /></Button></header>
      {channels === undefined || access === undefined ? <p className="loading-hint">Loading channel access…</p> : <ChannelAccessPicker channels={channels} mode={mode} selectedIds={selectedIds} onModeChange={setMode} onSelectionChange={setSelectedIds} />}
      {notice !== null ? <p className="form-notice" role="status">{notice}</p> : null}
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
      <div className="dialog__actions"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || channels === undefined || access === undefined || (mode === "selected" && selectedIds.length === 0)} onClick={() => { void save(); }}>{busy ? "Saving…" : "Save channel access"}</Button></div>
    </section>
  );
}

function InvitationChannelAccessEditor({ invitationId, email, initialMode, initialChannelIds, onClose }: {
  invitationId: string;
  email: string;
  initialMode: "all" | "selected";
  initialChannelIds: string[];
  onClose: () => void;
}) {
  const channels = useQuery(api.communication.listChannelsForAdmin, {});
  const updateAccess = useMutation(api.communication.updateInvitationChannelAccess);
  const [mode, setMode] = useState<"all" | "selected">(initialMode);
  const [selectedIds, setSelectedIds] = useState<string[]>(initialChannelIds);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setError(null);
    try {
      await updateAccess({ invitationId, mode, channelIds: mode === "all" ? [] : selectedIds });
      onClose();
    } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title={`Channel access for ${email}`} onClose={onClose} wide>
      <div className="stack">
        <p className="nav-hint">Choose the channels this person can open when they join.</p>
        {channels === undefined ? <p className="loading-hint">Loading channels…</p> : <ChannelAccessPicker channels={channels} mode={mode} selectedIds={selectedIds} onModeChange={setMode} onSelectionChange={setSelectedIds} />}
        {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="dialog__actions"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || channels === undefined || (mode === "selected" && selectedIds.length === 0)} onClick={() => { void save(); }}>{busy ? "Saving…" : "Save invitation access"}</Button></div>
      </div>
    </Dialog>
  );
}

function AgentsTab() {
  const members = useQuery(api.communication.listMembers, {});
  const registerAgent = useMutation(api.communication.registerAgent);
  const rotate = useMutation(api.communication.rotateAgentCredential);
  const deactivate = useMutation(api.communication.deactivateMember);
  const [name, setName] = useState("");
  const [revealed, setRevealed] = useState<{ name: string; credential: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const agents = (members ?? []).filter((member) => member.kind === "openclaw");
  const run = async (action: () => Promise<unknown>) => { setError(null); try { await action(); } catch (caught) { setError(errorMessage(caught)); } };
  const copy = async () => { if (revealed === null) return; await navigator.clipboard.writeText(revealed.credential); setCopied(true); window.setTimeout(() => { setCopied(false); }, 1500); };
  return (
    <div className="stack">
      <p className="nav-hint">Each AI employee has its own private connection. Add it to channels like a person, then give it a different role, context, and tool access in each channel.</p>
      <form className="field-row" onSubmit={(event) => { event.preventDefault(); void run(async () => { const result = await registerAgent({ displayName: name.trim() }); setRevealed({ name: name.trim(), credential: result.credential }); setName(""); }); }}>
        <input name="agent-name" autoComplete="off" value={name} onChange={(event) => { setName(event.target.value); }} placeholder="Agent name, e.g. Codex" aria-label="Agent name" minLength={2} maxLength={40} required />
        <Button type="submit" variant="primary"><Bot size={14} /> Add AI teammate</Button>
      </form>
      {revealed !== null ? (
        <div className="credential-box" role="status">
          <strong>Credential for {revealed.name} — shown once.</strong>
          <p>Copy this into the secure AI-worker setup on its host. Anyone with this value can act as this AI employee, so never send it in chat or email.</p>
          <div className="field-row"><code className="credential">{revealed.credential}</code><Button size="compact" onClick={() => { void copy(); }}>{copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}</Button><Button size="compact" variant="ghost" onClick={() => { setRevealed(null); }}>Done</Button></div>
        </div>
      ) : null}
      <table className="data-table">
        <thead><tr><th>Agent</th><th>Status</th><th aria-label="Actions" /></tr></thead>
        <tbody>
          {agents.length === 0 ? <tr><td colSpan={3} className="nav-hint">No AI teammates yet.</td></tr> : null}
          {agents.map((agent) => (
            <tr key={agent.principalId} className={agent.active ? undefined : "is-inactive"}>
              <td><span className="cell-person"><Avatar name={agent.displayName} kind="openclaw" small /><strong>{agent.displayName}</strong></span></td>
              <td><span className={agent.agentOnline === true ? "status-badge status-badge--green" : "status-badge"}>{!agent.active ? "removed" : agent.agentOnline === true ? "online" : "offline"}</span></td>
              <td className="cell-actions">
                {agent.active ? <Button size="compact" variant="ghost" onClick={() => { if (window.confirm(`Rotate ${agent.displayName}'s credential? The old one stops working immediately.`)) void run(async () => { const result = await rotate({ principalId: agent.principalId }); setRevealed({ name: agent.displayName, credential: result.credential }); }); }}><KeyRound size={13} /> Rotate credential</Button> : null}
                {agent.active ? <Button size="compact" variant="ghost" onClick={() => { if (window.confirm(`Remove ${agent.displayName}? It is locked out immediately.`)) void run(() => deactivate({ principalId: agent.principalId })); }}><UserMinus size={13} /> Remove</Button> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
    </div>
  );
}

function SecretsTab() {
  const secrets = useQuery(api.secrets.listSecrets, {});
  const setSecret = useAction(api.secrets.setSecret);
  const deleteSecret = useMutation(api.secrets.deleteSecret);
  const [name, setName] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true); setError(null); setNotice(null);
    try { await action(); setNotice(success); } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };
  const exists = (secrets ?? []).some((secret) => secret.name === name);
  return (
    <div className="stack">
      <p className="nav-hint">Passwords and keys your AI employees need for your other systems. Values are encrypted, belong to this workspace only, and cannot be viewed again after saving. To change one, save a new value under the same name.</p>
      <form className="field-row" onSubmit={(event) => { event.preventDefault(); void run(async () => { await setSecret({ name, value }); setName(""); setValue(""); }, exists ? `${name} replaced.` : `${name} saved.`); }}>
        <input name="secret-name" autoComplete="off" spellCheck={false} value={name} onChange={(event) => { setName(event.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_")); }} placeholder="NAME, e.g. ACUBLISS_PASSWORD" aria-label="Secret name" pattern="[A-Z][A-Z0-9_]{1,63}" minLength={2} maxLength={64} required />
        <input name="secret-value" type="password" autoComplete="new-password" value={value} onChange={(event) => { setValue(event.target.value); }} placeholder="Value" aria-label="Secret value" maxLength={8192} required />
        <Button type="submit" variant="primary" disabled={busy}><KeyRound size={14} /> {exists ? "Replace" : "Save"}</Button>
      </form>
      <table className="data-table">
        <thead><tr><th>Name</th><th>Last changed</th><th>Last used</th><th aria-label="Actions" /></tr></thead>
        <tbody>
          {secrets?.length === 0 ? <tr><td colSpan={4} className="nav-hint">No secrets yet.</td></tr> : null}
          {(secrets ?? []).map((secret) => (
            <tr key={secret.name}>
              <td><span className="cell-person"><Lock size={13} /><strong>{secret.name}</strong></span></td>
              <td>{new Date(secret.updatedAt).toLocaleDateString()}</td>
              <td>{secret.lastUsedAt === null ? "Never" : new Date(secret.lastUsedAt).toLocaleDateString()}</td>
              <td className="cell-actions"><Button size="compact" variant="ghost" disabled={busy} onClick={() => { if (window.confirm(`Delete ${secret.name}? Anything that uses it stops working immediately.`)) void run(() => deleteSecret({ name: secret.name }), `${secret.name} deleted.`); }}><X size={13} /> Delete</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {notice !== null ? <p className="nav-hint" role="status">{notice}</p> : null}
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
    </div>
  );
}

type EvidenceKey = "platform_baa" | "ai_provider_baa" | "third_party_baas" | "risk_assessment" | "privacy_security_policies" | "workforce_access_review" | "backup_restore" | "incident_response_drill" | "independent_review";
const evidenceKeys = new Set<string>(["platform_baa", "ai_provider_baa", "third_party_baas", "risk_assessment", "privacy_security_policies", "workforce_access_review", "backup_restore", "incident_response_drill", "independent_review"]);
function isEvidenceKey(value: string): value is EvidenceKey { return evidenceKeys.has(value); }

function SecurityTab({ me }: { me: MeReady }) {
  const overview = useQuery(api.communication.securityOverview, {});
  const audit = useQuery(api.communication.listAuditEvents, { limit: 40 });
  const [exportRequested, setExportRequested] = useState(false);
  const auditExport = useQuery(api.communication.listAuditEvents, exportRequested ? { limit: 2000 } : "skip");
  const updateSettings = useMutation(api.communication.updateSecuritySettings);
  const recordEvidence = useMutation(api.communication.recordComplianceEvidence);
  const activate = useMutation(api.communication.activateProtectedControls);
  const [inactivity, setInactivity] = useState(15);
  const [contentDays, setContentDays] = useState(365);
  const [auditDays, setAuditDays] = useState(2190);
  const [legalHold, setLegalHold] = useState(false);
  const [emergencyStopped, setEmergencyStopped] = useState(false);
  const [references, setReferences] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (overview === undefined) return;
    setInactivity(overview.inactivityLockMinutes);
    setContentDays(overview.retention.contentDays);
    setAuditDays(overview.retention.auditDays);
    setLegalHold(overview.retention.legalHold);
    setEmergencyStopped(overview.emergencyStopped);
  }, [overview]);

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true); setError(null); setNotice(null);
    try { await action(); setNotice(success); } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  };
  const verified = overview?.gates.filter((gate) => gate.status === "verified").length ?? 0;
  const total = overview?.gates.length ?? 13;
  const recordGate = (key: string, label: string) => {
    if (!isEvidenceKey(key)) return;
    void run(() => recordEvidence({ key, reference: references[key] ?? "" }), `${label} recorded.`);
  };
  useEffect(() => {
    if (!exportRequested || auditExport === undefined) return;
    const quote = (value: string) => `"${value.replaceAll('"', '""')}"`;
    const rows = [["Time", "Person", "Action", "Resource", "Result"], ...auditExport.map((event) => [new Date(event.occurredAt).toISOString(), event.actorName, event.action, event.resourceType, event.result])];
    const blob = new Blob([rows.map((row) => row.map(quote).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob); const link = document.createElement("a"); link.href = url; link.download = `yodl-audit-${new Date().toISOString().slice(0, 10)}.csv`; link.click(); URL.revokeObjectURL(url); setExportRequested(false);
  }, [auditExport, exportRequested]);

  return (
    <div className="security-tab stack">
      <div className="security-summary">
        <div className={`readiness-score${overview?.complianceMode === "hipaa" ? " is-ready" : ""}`}><ShieldCheck size={22} /><strong>{verified}/{total}</strong><span>readiness checks</span></div>
        <div><h3>{overview?.complianceMode === "hipaa" ? "Protected-data controls active" : "Production HIPAA readiness"}</h3><p>Yodl will not call itself HIPAA-ready from code alone. Vendor agreements and operational proof must be recorded before protected channels unlock.</p></div>
      </div>
      {me.billing?.trial === true ? <p className="form-notice" role="status">Free trial: protected health information is off and the readiness gates below stay read-only until your first payment and your signed BAA (we email it after the trial converts). Everything else can be set up now.</p> : null}

      <div className="readiness-grid">
        {(overview?.gates ?? []).map((gate) => (
          <div key={gate.key} className={`readiness-gate readiness-gate--${gate.status}`}>
            {gate.status === "verified" ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            <div><strong>{gate.label}</strong><span>{gate.status === "verified" ? gate.reference ?? "Implemented and active" : "Evidence still required"}</span></div>
            {gate.status === "pending" && isEvidenceKey(gate.key) && me.role === "owner" ? (
              <div className="evidence-entry"><input name={`evidence-${gate.key}`} value={references[gate.key] ?? ""} onChange={(event) => { setReferences((current) => ({ ...current, [gate.key]: event.target.value })); }} placeholder="Contract or test reference" aria-label={`Evidence for ${gate.label}`} /><Button size="compact" disabled={busy || (references[gate.key]?.trim().length ?? 0) < 4} onClick={() => { recordGate(gate.key, gate.label); }}>Record</Button></div>
            ) : null}
          </div>
        ))}
      </div>

      <section className="security-settings">
        <div className="section-heading"><div><strong>Security controls</strong><span>These settings are enforced for the whole workspace.</span></div></div>
        <div className="settings-grid">
          <label className="field">Automatic sign-out (minutes)<input type="number" min={5} max={480} value={inactivity} onChange={(event) => { setInactivity(Number(event.target.value)); }} /></label>
          <label className="field">Message retention (days)<input type="number" min={1} max={3650} value={contentDays} onChange={(event) => { setContentDays(Number(event.target.value)); }} /></label>
          <label className="field">Audit retention (days)<input type="number" min={365} max={3650} value={auditDays} onChange={(event) => { setAuditDays(Number(event.target.value)); }} /></label>
          <label className="check"><input type="checkbox" checked={legalHold} onChange={(event) => { setLegalHold(event.target.checked); }} /> Legal hold (prevents retention deletion)</label>
          <label className="check check--danger"><input type="checkbox" checked={emergencyStopped} onChange={(event) => { setEmergencyStopped(event.target.checked); }} /> Pause every AI employee</label>
        </div>
        <div className="dialog__actions dialog__actions--spread">
          <span className="nav-hint">{overview?.agentCount ?? 0} AI employees · {overview?.reviewedAgentCount ?? 0} approved for protected channels</span>
          <Button variant="primary" disabled={busy} onClick={() => { void run(() => updateSettings({ inactivityLockMinutes: inactivity, contentDays, auditDays, legalHold, emergencyStopped }), "Security settings saved."); }}>Save security settings</Button>
        </div>
      </section>

      {overview?.complianceMode !== "hipaa" && overview?.readyForProtectedControls === true && me.role === "owner" ? (
        <div className="activation-box"><div><strong>All recorded gates are complete.</strong><p>This activates 15-minute sign-out and unlocks PHI channel labels. It is a technical control switch, not a legal certification.</p></div><Button variant="primary" disabled={busy} onClick={() => { if (window.confirm("Activate protected-data controls for this workspace?")) void run(() => activate({ confirmation: "ENABLE PROTECTED DATA CONTROLS" }), "Protected-data controls activated."); }}>Activate protected-data controls</Button></div>
      ) : null}
      {notice !== null ? <p className="form-notice" role="status">{notice}</p> : null}
      {error !== null ? <p className="form-error" role="alert">{error}</p> : null}

      <section>
        <div className="section-heading"><div><strong>Recent security activity</strong><span>Latest access, permission, message, and AI events.</span></div><Button size="compact" variant="ghost" disabled={exportRequested} onClick={() => { setExportRequested(true); }}><Download size={13} /> Export audit CSV</Button></div>
        <div className="audit-list">
          {(audit ?? []).map((event) => <div key={event.id}><span className={`audit-result audit-result--${event.result}`} /><strong>{event.action.replaceAll(".", " · ").replaceAll("_", " ")}</strong><span>{event.actorName}</span><time>{new Date(event.occurredAt).toLocaleString()}</time></div>)}
          {audit !== undefined && audit.length === 0 ? <p className="nav-hint">No security activity yet.</p> : null}
        </div>
      </section>
    </div>
  );
}
